// src/components/search-results.tsx
//
// The topbar search box — was a plain, non-functional `<input>` with just a
// placeholder (no state, no query, nothing). Wired to two real endpoints:
//   - GET profile/users?search= for people — profile/creators (CREATOR-role
//     only) was the wrong directory for this same reason collections.tsx's
//     Following tab already flagged: a search should find ANY account, not
//     just creators.
//   - GET contents?search= for posts — deliberately the OLD, deprecated
//     listing, NOT contents/feed. The feed doc's Step 2 ("remove anything
//     the user has already seen") applies unconditionally, even through a
//     filter — so searching for something you'd already viewed would
//     silently come back empty. A search has to return everything that
//     matches regardless of view history; contents/feed is for the ranked,
//     no-repeats home feed, not this. See profile.tsx/creator.tsx for the
//     same reasoning, applied for the same reason.
// Debounced client-side (300ms) since neither endpoint is a dedicated
// search-as-you-type API; a real debounce endpoint/suggestion API would
// replace this if one exists.

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, Icon } from "@/lib/ui";
import { useGetData } from "@/hooks/api/use-api";
import { useT } from "@/lib/core";
import type { CreatorUser } from "@/utils/types";
import type { RawContent } from "@/lib/adapters/content";

export function SearchResults() {
  const t = useT();
  const navigate = useNavigate();
  const wrapRef = useRef<HTMLDivElement>(null);

  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"People" | "Posts">("People");

  useEffect(() => {
    const id = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(id);
  }, [query]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const { data: creatorsData, isLoading: creatorsLoading } = useGetData({
    url: `profile/users?search=${encodeURIComponent(debounced)}&size=15`,
    queryKey: ["SearchCreators", debounced],
    enabled: debounced.length > 0,
  });
  const { data: postsData, isLoading: postsLoading } = useGetData({
    url: `contents?search=${encodeURIComponent(debounced)}&size=15`,
    queryKey: ["SearchPosts", debounced],
    enabled: debounced.length > 0,
  });

  const creators: CreatorUser[] =
    (creatorsData as { data?: { content?: CreatorUser[] } } | undefined)?.data
      ?.content ?? [];
  const posts: RawContent[] =
    (postsData as { data?: { content?: RawContent[] } } | undefined)?.data
      ?.content ?? [];

  const isLoading = creatorsLoading || postsLoading;
  const hasResults = creators.length > 0 || posts.length > 0;
  const showPanel = open && debounced.length > 0;

  const close = () => {
    setOpen(false);
    setQuery("");
    setDebounced("");
    setTab("People");
  };

  return (
    <div ref={wrapRef} style={{ position: "relative", flex: 1, maxWidth: 420 }}>
      <div className="search" style={{ maxWidth: "none" }}>
        <Icon n="search" s={17} />
        <input
          placeholder={t("search_placeholder")}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => query && setOpen(true)}
        />
        {query && (
          <button
            className="muted"
            onClick={() => {
              setQuery("");
              setDebounced("");
            }}
            aria-label="Clear search"
          >
            <Icon n="x" s={14} />
          </button>
        )}
      </div>

      {showPanel && (
        <div
          className="card"
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            right: 0,
            zIndex: 140,
            padding: 0,
            maxHeight: 460,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            // .card's own background (--card) is a near-transparent tint
            // meant to sit over the plain page background — fine in flow,
            // but this panel floats over arbitrary scrolled feed content
            // (exactly the hazard chat-popups.tsx's identical note already
            // flagged), which bled straight through it. --card2 is the
            // solid surface Menu/modals already use for exactly this.
            background: "var(--card2)",
            boxShadow: "0 14px 44px rgba(0,0,0,.45)",
          }}
        >
          <div
            className="row gap16"
            style={{
              padding: "4px 12px",
              borderBottom: "1px solid var(--line)",
              flexShrink: 0,
            }}
          >
            {(["People", "Posts"] as const).map((tb) => (
              <button
                key={tb}
                onClick={() => setTab(tb)}
                style={{
                  padding: "10px 2px",
                  fontWeight: 600,
                  fontSize: 13,
                  color: tab === tb ? "var(--text)" : "var(--muted)",
                  borderBottom:
                    tab === tb
                      ? "2px solid var(--blue-ink)"
                      : "2px solid transparent",
                }}
              >
                {tb}
                {(tb === "People" ? creators.length : posts.length) > 0 &&
                  ` (${tb === "People" ? creators.length : posts.length})`}
              </button>
            ))}
          </div>

          <div className="no-scrollbar" style={{ overflowY: "auto", padding: 8 }}>
            {isLoading && (
              <div className="muted t13" style={{ padding: "10px 8px" }}>
                Searching…
              </div>
            )}

            {!isLoading && !hasResults && (
              <div className="muted t13" style={{ padding: "10px 8px" }}>
                No results for "{debounced}"
              </div>
            )}

            {!isLoading && tab === "People" && creators.length > 0 && (
              <div className="col gap2">
                {creators.map((c) => (
                  <div
                    key={c.publicId}
                    className="row gap10"
                    style={{ padding: "8px", borderRadius: 10, cursor: "pointer" }}
                    onClick={() => {
                      close();
                      navigate(`/creator/${c.username}`);
                    }}
                  >
                    <Avatar name={c.fullName} src={c.profileImageUrl} size={32} initialsOnly />
                    <div className="col" style={{ minWidth: 0 }}>
                      <div
                        className="t13 b6"
                        style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      >
                        {c.fullName}
                      </div>
                      <div className="muted t12">@{c.username}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {!isLoading && tab === "People" && creators.length === 0 && hasResults && (
              <div className="muted t13" style={{ padding: "10px 8px" }}>
                No people match "{debounced}"
              </div>
            )}

            {!isLoading && tab === "Posts" && posts.length > 0 && (
              <div className="col gap2">
                {posts.map((p) => (
                  <div
                    key={p.publicId}
                    className="row gap10"
                    style={{ padding: "8px", borderRadius: 10, cursor: "pointer" }}
                    onClick={() => {
                      close();
                      navigate(`/feed/${p.publicId}`);
                    }}
                  >
                    <Avatar name={p.creator?.name} src={p.creator?.profilePic} size={32} initialsOnly />
                    <div className="col" style={{ minWidth: 0 }}>
                      <div
                        className="t13 b6"
                        style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      >
                        {p.creator?.name}
                      </div>
                      <div
                        className="muted t12"
                        style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      >
                        {p.message || "(no caption)"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {!isLoading && tab === "Posts" && posts.length === 0 && hasResults && (
              <div className="muted t13" style={{ padding: "10px 8px" }}>
                No posts match "{debounced}"
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
